// src/hooks/useAgentChat.js

import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import axios from 'axios';

const BACKEND_URL = 'https://api.fitoja.in'; 

export const useAgentChat = () => {
    const [activeConversation, setActiveConversation] = useState(null); 
    const [messages, setMessages] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const socketRef = useRef(null);

    // 1. Connection and Setup
    // 💡 FIX: Removed 'activeConversation' from dependency array 
    // to prevent continuous re-running, and used setter function inside listener.
    useEffect(() => {
        // Initialize socket and connect
        socketRef.current = io(BACKEND_URL);

        socketRef.current.on('connect', () => {
            setIsConnected(true);
            console.log('Agent Socket connected');
        });

        // 2. Message Receiving (The fix for not showing customer messages)
        socketRef.current.on('receive_message', (message) => {
            console.log('Received message:', message);
            
            // 💡 FIX: Use the functional form of the setter to get the LATEST activeConversation state.
            setActiveConversation(prevConvo => {
                // Check if the incoming message belongs to the room we are currently viewing/active in
                if (prevConvo && message.roomId === prevConvo.roomId) {
                    setMessages((prevMessages) => [...prevMessages, message]);
                }
                // Return the previous state to maintain consistency
                return prevConvo;
            });
        });
        
        // 3. History Receiving (Fired only when agent:join_room is processed)
        socketRef.current.on('history', (historyMessages) => {
            console.log('Received history:', historyMessages);
            setMessages(historyMessages);
        });

        socketRef.current.on('disconnect', () => {
            setIsConnected(false);
        });

        // Cleanup: Remove listeners before component unmounts
        return () => {
            if (socketRef.current) {
                // Remove specific listeners to prevent memory leaks and stale callbacks
                socketRef.current.off('receive_message');
                socketRef.current.off('history');
                socketRef.current.disconnect(); 
            }
        };
    }, []); // Empty dependency array ensures listeners are set up once

    // Helper function to join the room and request history
    const joinChatRoom = useCallback((roomId) => {
        if (socketRef.current) {
            setMessages([]); // Clear messages immediately on joining new room
            socketRef.current.emit('agent:join_room', roomId);
        }
    }, []);

    // 4. Claim and Join Logic
    const claimAndJoinChat = useCallback(async (conversation) => {
        // 💡 Local Storage Fix: Read the current agentId from local storage here
        const currentAgentId = localStorage.getItem('agentId'); 

        if (!currentAgentId) {
            console.error("AGENT_ID is missing. Cannot claim chat.");
            alert("Agent not logged in or ID is missing.");
            return;
        }

        const conversationId = conversation._id;
        const roomId = conversation.roomId;

        try {
            setMessages([]); 

            // REST API Call: 1. Claim the conversation
            await axios.post(`${BACKEND_URL}/api/chat/admin/claim/${conversationId}`, { agentId: currentAgentId });
            
            // Set the conversation as active in the UI state
            setActiveConversation({ ...conversation, status: 'Assigned', agentId: currentAgentId });
            
            // Socket.IO Event: 2. Join the chat room on the server
            if (socketRef.current) {
                socketRef.current.emit('agent:join_room', roomId);
            }
            // Messages will be set via the 'history' listener

             // 💡 Use the new helper function
             joinChatRoom(roomId);

        } catch (error) {
            console.error("Error claiming chat:", error);
            alert("Failed to claim chat.");
        }
    }, []);

    // 5. Send Message Function
    const sendMessage = (text) => {
        if (!activeConversation || !isConnected || !text.trim()) return;

        const messageData = {
            // 💡 FIX: Ensure roomId is included
            roomId: activeConversation.roomId, 
            senderType: 'Agent', 
            text: text,
        };
        
        // 💡 FIX: Optimistically add the message with necessary fields for rendering
        // const optimisticMessage = {
        //      ...messageData,
        //      _id: Date.now().toString(), // Mock ID
        //      createdAt: new Date().toISOString(), // Necessary for rendering time/sorting
        // };
        // setMessages((prevMessages) => [...prevMessages, optimisticMessage]);
        socketRef.current.emit('send_message', messageData);
    };


    return {
        isConnected,
        activeConversation,
        messages,
        claimAndJoinChat,
        sendMessage,
        setActiveConversation, 
        setMessages, // <--- Must be here
        joinChatRoom, // 💡 RETURN THE NEW FUNCTION
    };
};